/** Realistic seed data for Jivandhara / Jivandhara Old Age Home prototype. */

export const STORAGE_KEY = "jivandhara-local-db";

const d = (daysAgo: number) =>
  new Date(Date.now() - daysAgo * 86_400_000).toISOString();

const day = (daysFromNow: number) => {
  const x = new Date();
  x.setDate(x.getDate() + daysFromNow);
  return x.toISOString().slice(0, 10);
};

// Stable UUIDs for cross-table references
export const IDS = {
  adminUser: "a1000000-0000-4000-8000-000000000001",
  donorUser: "a1000000-0000-4000-8000-000000000002",
  typeGeneral: "b1000000-0000-4000-8000-000000000001",
  typeAnnadan: "b1000000-0000-4000-8000-000000000002",
  typeInfrastructure: "b1000000-0000-4000-8000-000000000003",
  catGeneral: "c1000000-0000-4000-8000-000000000001",
  catAnnadan: "c1000000-0000-4000-8000-000000000002",
  catHealthcare: "c1000000-0000-4000-8000-000000000003",
  catInfrastructure: "c1000000-0000-4000-8000-000000000004",
  campaignWard: "d1000000-0000-4000-8000-000000000001",
  campaignSolar: "d1000000-0000-4000-8000-000000000002",
  campaignAnnadan: "d1000000-0000-4000-8000-000000000003",
  infraLift: "e1000000-0000-4000-8000-000000000001",
  infraKitchen: "e1000000-0000-4000-8000-000000000002",
  infraAmbulance: "e1000000-0000-4000-8000-000000000003",
  sevaTiffin: "f1000000-0000-4000-8000-000000000001",
  sevaMedical: "f1000000-0000-4000-8000-000000000002",
  sevaBirthday: "f1000000-0000-4000-8000-000000000003",
  eventAnnadan: "g1000000-0000-4000-8000-000000000001",
  eventHealthCamp: "g1000000-0000-4000-8000-000000000002",
  eventParyushan: "g1000000-0000-4000-8000-000000000003",
  donor1: "h1000000-0000-4000-8000-000000000001",
  donor2: "h1000000-0000-4000-8000-000000000002",
  donor3: "h1000000-0000-4000-8000-000000000003",
  donor4: "h1000000-0000-4000-8000-000000000004",
  donor5: "h1000000-0000-4000-8000-000000000005",
  resident1: "i1000000-0000-4000-8000-000000000001",
  resident2: "i1000000-0000-4000-8000-000000000002",
  resident3: "i1000000-0000-4000-8000-000000000003",
  resident4: "i1000000-0000-4000-8000-000000000004",
  paymentSettings: "j1000000-0000-4000-8000-000000000001",
} as const;

export type TableName =
  | "donation_types"
  | "donation_categories"
  | "campaigns"
  | "infrastructure_items"
  | "seva_options"
  | "events"
  | "donors"
  | "donations"
  | "recurring_donations"
  | "trustees"
  | "committee_members"
  | "gallery"
  | "posts"
  | "payment_settings"
  | "residents"
  | "resident_family"
  | "contact_inquiries"
  | "feedback"
  | "tiffin_requests"
  | "inkind_requests"
  | "user_roles"
  | "profiles"
  | "receipts";

export type DbRow = Record<string, unknown> & { id: string; created_at?: string };

/** Row payload for inserts — `id` / `created_at` are optional and auto-filled. */
export type DbInsert = Record<string, unknown> & {
  id?: string;
  created_at?: string;
};

/** Row payload for partial updates. */
export type DbUpdate = Record<string, unknown>;

export type LocalDb = Record<TableName, DbRow[]>;

function donation(
  id: string,
  donorId: string,
  amount: number,
  daysAgo: number,
  extra: Record<string, unknown> = {}
): DbRow {
  const createdAt = d(daysAgo);
  return {
    id,
    created_at: createdAt,
    receipt_no: `MSV-${2025}${String(daysAgo).padStart(4, "0")}`,
    amount,
    donation_date: createdAt.slice(0, 10),
    donor_id: donorId,
    payment_mode: daysAgo % 3 === 0 ? "UPI" : daysAgo % 3 === 1 ? "Cash" : "Online",
    payment_status: daysAgo % 7 === 0 ? "pending" : "success",
    category: daysAgo % 2 === 0 ? "general" : "annadan",
    purpose: "Jivandhara — Jivandhara Old Age Home",
    requires_80g: true,
    ...extra,
  };
}

export function createSeedData(): LocalDb {
  const donationIds = Array.from({ length: 8 }, (_, i) =>
    `k1000000-0000-4000-8000-${String(i + 1).padStart(12, "0")}`
  );

  return {
    donation_types: [
      {
        id: IDS.typeGeneral,
        created_at: d(365),
        name: "General Donation",
        slug: "general",
        kind: "general",
        description: "Support daily operations, resident care, and trust activities at Jivandhara.",
        icon: "heart-handshake",
        is_active: true,
        is_amount_required: true,
        is_inkind: false,
        is_target_applicable: false,
        is_recognition_applicable: true,
        is_80g_applicable: true,
        is_recurring_allowed: true,
        default_frequency: "monthly",
        min_amount: 101,
        suggested_amounts: [501, 1100, 2100, 5100, 11000],
        display_order: 1,
      },
      {
        id: IDS.typeAnnadan,
        created_at: d(360),
        name: "Annadan Seva",
        slug: "annadan",
        kind: "seva",
        description: "Sponsor wholesome meals for elders — a sacred act of Jivandhara.",
        icon: "utensils",
        is_active: true,
        is_amount_required: true,
        is_inkind: false,
        is_target_applicable: true,
        is_recognition_applicable: true,
        is_80g_applicable: true,
        is_recurring_allowed: true,
        default_frequency: "monthly",
        min_amount: 501,
        suggested_amounts: [1100, 2100, 5100, 11000],
        display_order: 2,
      },
      {
        id: IDS.typeInfrastructure,
        created_at: d(355),
        name: "Infrastructure & Bhumi Dan",
        slug: "infrastructure",
        kind: "infrastructure",
        description: "Contribute towards building upgrades, medical facilities, and campus development.",
        icon: "building",
        is_active: true,
        is_amount_required: true,
        is_inkind: false,
        is_target_applicable: true,
        is_recognition_applicable: true,
        is_80g_applicable: true,
        is_recurring_allowed: false,
        min_amount: 1100,
        suggested_amounts: [5100, 11000, 51000, 110000],
        display_order: 3,
      },
    ],

    donation_categories: [
      {
        id: IDS.catGeneral,
        created_at: d(350),
        name: "General Welfare",
        slug: "general-welfare",
        description: "Unrestricted support for resident care and trust operations.",
        is_active: true,
        featured_on_home: true,
        display_order: 1,
        target_amount: 500000,
        suggested_amounts: [501, 1100, 2100],
      },
      {
        id: IDS.catAnnadan,
        created_at: d(348),
        name: "Annadan",
        slug: "annadan",
        description: "Daily meal sponsorship for all residents.",
        is_active: true,
        featured_on_home: true,
        display_order: 2,
        target_amount: 300000,
        suggested_amounts: [1100, 2100, 5100],
      },
      {
        id: IDS.catHealthcare,
        created_at: d(346),
        name: "Healthcare & Medical",
        slug: "healthcare",
        description: "Medicines, doctor visits, and emergency medical care for elders.",
        is_active: true,
        featured_on_home: false,
        display_order: 3,
        target_amount: 250000,
        suggested_amounts: [2100, 5100, 11000],
      },
      {
        id: IDS.catInfrastructure,
        created_at: d(344),
        name: "Infrastructure",
        slug: "infrastructure",
        description: "Campus development, equipment, and facility upgrades.",
        is_active: true,
        featured_on_home: true,
        display_order: 4,
        target_amount: 1500000,
        suggested_amounts: [5100, 11000, 51000],
      },
    ],

    campaigns: [
      {
        id: IDS.campaignWard,
        created_at: d(200),
        title: "New Medical Ward — Jivandhara",
        slug: "medical-ward-jivandhara",
        description: "Help us build a 20-bed medical ward with oxygen support and nursing station for elderly residents.",
        category_id: IDS.catHealthcare,
        target_amount: 800000,
        collected_amount: 342500,
        status: "active",
        is_featured: true,
        show_progress: true,
        display_order: 1,
        start_date: day(-60),
        end_date: day(120),
      },
      {
        id: IDS.campaignSolar,
        created_at: d(180),
        title: "Solar Power for Campus",
        slug: "solar-power-campus",
        description: "Install rooftop solar panels to reduce electricity costs and ensure uninterrupted power for medical equipment.",
        category_id: IDS.catInfrastructure,
        target_amount: 450000,
        collected_amount: 128000,
        status: "active",
        is_featured: true,
        show_progress: true,
        display_order: 2,
        start_date: day(-30),
        end_date: day(90),
      },
      {
        id: IDS.campaignAnnadan,
        created_at: d(160),
        title: "365 Days Annadan Fund",
        slug: "365-annadan-fund",
        description: "Sponsor one day of Annadan for all 48 residents — ₹12,000 per day.",
        category_id: IDS.catAnnadan,
        target_amount: 4380000,
        collected_amount: 890000,
        status: "active",
        is_featured: false,
        show_progress: true,
        display_order: 3,
        start_date: day(-90),
        end_date: day(275),
      },
    ],

    infrastructure_items: [
      {
        id: IDS.infraLift,
        created_at: d(150),
        title: "Hydraulic Lift Installation",
        description: "Accessible lift for elderly residents on upper floors.",
        full_description: "A hydraulic lift connecting ground to first floor, enabling wheelchair and walker access for mobility-challenged elders.",
        category_id: IDS.catInfrastructure,
        required_amount: 650000,
        collected_amount: 215000,
        status: "in_progress",
        is_active: true,
        display_on_website: true,
        display_order: 1,
        partial_allowed: true,
        recognition_applicable: true,
        recognition_text: "Donor name plaque at lift entrance",
      },
      {
        id: IDS.infraKitchen,
        created_at: d(145),
        title: "Commercial Kitchen Upgrade",
        description: "Modern kitchen equipment for hygienic meal preparation.",
        full_description: "Stainless steel counters, industrial chimney, and refrigeration for daily Annadan serving 150+ meals.",
        category_id: IDS.catInfrastructure,
        required_amount: 320000,
        collected_amount: 95000,
        status: "open",
        is_active: true,
        display_on_website: true,
        display_order: 2,
        partial_allowed: true,
        recognition_applicable: false,
      },
      {
        id: IDS.infraAmbulance,
        created_at: d(140),
        title: "Ambulance Van",
        description: "Dedicated ambulance for emergency hospital transfers.",
        full_description: "Fully equipped ambulance with oxygen and stretcher for 24×7  resident medical emergencies.",
        category_id: IDS.catHealthcare,
        required_amount: 900000,
        collected_amount: 180000,
        status: "open",
        is_active: true,
        display_on_website: true,
        display_order: 3,
        partial_allowed: false,
        recognition_applicable: true,
        recognition_text: "Vehicle branding with donor recognition",
      },
    ],

    seva_options: [
      {
        id: IDS.sevaTiffin,
        created_at: d(130),
        title: "Tiffin Seva — One Day",
        description: "Sponsor tiffin meals for residents on a chosen date.",
        category_id: IDS.catAnnadan,
        amount_type: "fixed",
        fixed_amount: 12000,
        min_amount: null,
        date_required: true,
        occasion_required: false,
        in_memory_option: true,
        is_active: true,
        display_order: 1,
        suggested_amounts: [12000],
      },
      {
        id: IDS.sevaMedical,
        created_at: d(125),
        title: "Medical Camp Sponsorship",
        description: "Sponsor a free health check-up camp for residents and nearby elders.",
        category_id: IDS.catHealthcare,
        amount_type: "minimum",
        fixed_amount: null,
        min_amount: 15000,
        date_required: true,
        occasion_required: false,
        in_memory_option: false,
        is_active: true,
        display_order: 2,
        suggested_amounts: [15000, 25000, 51000],
      },
      {
        id: IDS.sevaBirthday,
        created_at: d(120),
        title: "Birthday / Punya Tithi Seva",
        description: "Celebrate a birthday or commemorate a departed soul with Annadan.",
        category_id: IDS.catAnnadan,
        amount_type: "minimum",
        fixed_amount: null,
        min_amount: 2100,
        date_required: true,
        occasion_required: true,
        in_memory_option: true,
        is_active: true,
        display_order: 3,
        suggested_amounts: [2100, 5100, 11000],
      },
    ],

    events: [
      {
        id: IDS.eventAnnadan,
        created_at: d(100),
        title: "Mahavir Jayanti Annadan",
        slug: "mahavir-jayanti-annadan-2026",
        short_description: "Community Annadan on the auspicious occasion of Mahavir Jayanti.",
        full_description: "Join us for a grand Annadan serving 500+ meals to residents, visitors, and community members.",
        event_type: "annadan",
        event_date: day(45),
        start_time: "10:00",
        end_time: "14:00",
        venue: "Jivandhara Campus, Ahmedabad",
        organizer: "Jivandhara Trust",
        contact_no: "+91 98765 43210",
        goal_amount: 75000,
        raised_amount: 28500,
        publish: true,
        status: "upcoming",
        donation_amount_options: [501, 1100, 2100, 5100],
      },
      {
        id: IDS.eventHealthCamp,
        created_at: d(95),
        title: "Free Health Check-up Camp",
        slug: "health-camp-2026",
        short_description: "Multi-specialty health camp for elders — eye, dental, and general check-up.",
        full_description: "Partner doctors from Ahmedabad will provide free consultations for residents and local senior citizens.",
        event_type: "healthcare",
        event_date: day(20),
        start_time: "09:00",
        end_time: "17:00",
        venue: "Jivandhara Medical Room",
        organizer: "Healthcare Committee",
        contact_no: "+91 98765 43211",
        goal_amount: 50000,
        raised_amount: 12000,
        publish: true,
        status: "upcoming",
        donation_amount_options: [1100, 2100, 5100],
      },
      {
        id: IDS.eventParyushan,
        created_at: d(90),
        title: "Paryushan Parva Celebrations",
        slug: "paryushan-2026",
        short_description: "Spiritual discourses and special meals during Paryushan.",
        full_description: "Eight days of Pravachan, Samayik, and specially prepared sattvic meals for all residents.",
        event_type: "spiritual",
        event_date: day(75),
        start_time: "06:00",
        end_time: "20:00",
        venue: "Jivandhara Prayer Hall",
        organizer: "Spiritual Committee",
        contact_no: "+91 98765 43212",
        goal_amount: 100000,
        raised_amount: 35000,
        publish: true,
        status: "upcoming",
        donation_amount_options: [501, 1100, 5100, 11000],
      },
    ],

    donors: [
      {
        id: IDS.donor1,
        created_at: d(300),
        full_name: "Ramesh Chimanlal Patel",
        donor_code: "DNR-001",
        mobile: "9876543210",
        email: "ramesh.patel@example.com",
        pan: "ABCPR1234F",
        address: "12, Shanti Society, Navrangpura",
        city: "Ahmedabad",
        state: "Gujarat",
        pincode: "380009",
        country: "India",
        donor_tier: "gold",
        jain_status: "Shwetambar",
        user_id: IDS.donorUser,
      },
      {
        id: IDS.donor2,
        created_at: d(280),
        full_name: "Kiran Jayant Shah",
        donor_code: "DNR-002",
        mobile: "9876543211",
        email: "kiran.shah@example.com",
        pan: "ABCKS5678G",
        address: "45, Ring Road",
        city: "Surat",
        state: "Gujarat",
        pincode: "395002",
        country: "India",
        donor_tier: "silver",
        jain_status: "Shwetambar",
        user_id: null,
      },
      {
        id: IDS.donor3,
        created_at: d(260),
        full_name: "Meena Kantilal Desai",
        donor_code: "DNR-003",
        mobile: "9876543212",
        email: "meena.desai@example.com",
        pan: "ABCMD9012H",
        address: "8, CG Road",
        city: "Ahmedabad",
        state: "Gujarat",
        pincode: "380006",
        country: "India",
        donor_tier: "bronze",
        user_id: null,
      },
      {
        id: IDS.donor4,
        created_at: d(240),
        full_name: "Vijay Kumar Mehta",
        donor_code: "DNR-004",
        mobile: "9876543213",
        email: "vijay.mehta@example.com",
        company: "Mehta Industries Pvt Ltd",
        city: "Vadodara",
        state: "Gujarat",
        country: "India",
        donor_tier: "platinum",
        jain_status: "Shwetambar",
        user_id: null,
      },
      {
        id: IDS.donor5,
        created_at: d(220),
        full_name: "Anjali Narendra Jain",
        donor_code: "DNR-005",
        mobile: "9876543214",
        email: "anjali.jain@example.com",
        city: "Rajkot",
        state: "Gujarat",
        country: "India",
        donor_tier: "silver",
        user_id: null,
      },
    ],

    donations: [
      donation(donationIds[0], IDS.donor1, 5100, 1, {
        donation_type_id: IDS.typeGeneral,
        donation_category_id: IDS.catGeneral,
        campaign_id: null,
      }),
      donation(donationIds[1], IDS.donor2, 3100, 2, {
        donation_type_id: IDS.typeAnnadan,
        donation_category_id: IDS.catAnnadan,
      }),
      donation(donationIds[2], IDS.donor3, 2500, 3, {
        donation_type_id: IDS.typeGeneral,
        donation_category_id: IDS.catGeneral,
      }),
      donation(donationIds[3], IDS.donor4, 11000, 4, {
        donation_type_id: IDS.typeInfrastructure,
        donation_category_id: IDS.catInfrastructure,
        campaign_id: IDS.campaignWard,
        infrastructure_item_id: IDS.infraLift,
      }),
      donation(donationIds[4], IDS.donor1, 2100, 5, {
        donation_type_id: IDS.typeAnnadan,
        donation_category_id: IDS.catAnnadan,
        seva_id: IDS.sevaTiffin,
        seva_date: day(-5),
      }),
      donation(donationIds[5], IDS.donor5, 5100, 6, {
        donation_type_id: IDS.typeGeneral,
        donation_category_id: IDS.catHealthcare,
        event_id: IDS.eventHealthCamp,
      }),
      donation(donationIds[6], IDS.donor2, 1100, 7, {
        donation_type_id: IDS.typeAnnadan,
        donation_category_id: IDS.catAnnadan,
        payment_status: "pending",
      }),
      donation(donationIds[7], IDS.donor3, 21000, 10, {
        donation_type_id: IDS.typeInfrastructure,
        donation_category_id: IDS.catInfrastructure,
        campaign_id: IDS.campaignSolar,
        frequency: "one-time",
      }),
    ],

    recurring_donations: [
      {
        id: "l1000000-0000-4000-8000-000000000001",
        created_at: d(120),
        donor_id: IDS.donor1,
        donation_type_id: IDS.typeGeneral,
        amount: 1100,
        frequency: "monthly",
        status: "active",
        start_date: day(-90),
        next_payment_date: day(15),
        last_payment_date: day(-15),
      },
      {
        id: "l1000000-0000-4000-8000-000000000002",
        created_at: d(100),
        donor_id: IDS.donor4,
        donation_type_id: IDS.typeAnnadan,
        campaign_id: IDS.campaignAnnadan,
        amount: 5100,
        frequency: "monthly",
        status: "active",
        start_date: day(-60),
        next_payment_date: day(8),
        last_payment_date: day(-22),
      },
    ],

    trustees: [
      {
        id: "m1000000-0000-4000-8000-000000000001",
        created_at: d(500),
        full_name: "Shri Kantilal R. Shah",
        designation: "Managing Trustee",
        trust_role: "Chairman",
        mobile: "9825012345",
        email: "trustee.shah@jivandhara.org",
        address: "Jivandhara Trust, Jivandhara Campus, Ahmedabad",
        joining_date: "2010-04-01",
        display: true,
        sort_order: 1,
        status: "active",
      },
      {
        id: "m1000000-0000-4000-8000-000000000002",
        created_at: d(498),
        full_name: "Smt. Rekha M. Patel",
        designation: "Trustee",
        trust_role: "Secretary",
        mobile: "9825012346",
        email: "rekha.patel@jivandhara.org",
        joining_date: "2012-06-15",
        display: true,
        sort_order: 2,
        status: "active",
      },
      {
        id: "m1000000-0000-4000-8000-000000000003",
        created_at: d(496),
        full_name: "Shri Narendra K. Jain",
        designation: "Trustee",
        trust_role: "Treasurer",
        mobile: "9825012347",
        email: "narendra.jain@jivandhara.org",
        joining_date: "2015-01-10",
        display: true,
        sort_order: 3,
        status: "active",
      },
      {
        id: "m1000000-0000-4000-8000-000000000004",
        created_at: d(494),
        full_name: "Dr. Harshad V. Mehta",
        designation: "Trustee — Healthcare",
        trust_role: "Medical Advisor",
        mobile: "9825012348",
        email: "harshad.mehta@jivandhara.org",
        joining_date: "2018-03-20",
        display: true,
        sort_order: 4,
        status: "active",
      },
    ],

    committee_members: [
      {
        id: "n1000000-0000-4000-8000-000000000001",
        created_at: d(400),
        full_name: "Priya Desai",
        designation: "Annadan Committee Head",
        role_responsibility: "Meal planning and vendor coordination",
        mobile: "9825023456",
        email: "priya.desai@jivandhara.org",
        display: true,
        sort_order: 1,
        status: "active",
      },
      {
        id: "n1000000-0000-4000-8000-000000000002",
        created_at: d(398),
        full_name: "Rajesh Kumar",
        designation: "Healthcare Coordinator",
        role_responsibility: "Doctor visits and medicine inventory",
        mobile: "9825023457",
        display: true,
        sort_order: 2,
        status: "active",
      },
      {
        id: "n1000000-0000-4000-8000-000000000003",
        created_at: d(396),
        full_name: "Sunita Shah",
        designation: "Resident Welfare",
        role_responsibility: "Daily resident needs and family liaison",
        mobile: "9825023458",
        display: true,
        sort_order: 3,
        status: "active",
      },
      {
        id: "n1000000-0000-4000-8000-000000000004",
        created_at: d(394),
        full_name: "Amit Bhatt",
        designation: "Fundraising & Donor Relations",
        role_responsibility: "Campaign outreach and donor communication",
        mobile: "9825023459",
        email: "amit.bhatt@jivandhara.org",
        display: true,
        sort_order: 4,
        status: "active",
      },
    ],

    gallery: [
      {
        id: "o1000000-0000-4000-8000-000000000001",
        created_at: d(80),
        title: "Morning Prayer at Jivandhara",
        category: "spiritual",
        image_url: "/placeholder.svg",
        sort_order: 1,
      },
      {
        id: "o1000000-0000-4000-8000-000000000002",
        created_at: d(78),
        title: "Annadan Seva — Daily Meals",
        category: "annadan",
        image_url: "/placeholder.svg",
        sort_order: 2,
      },
      {
        id: "o1000000-0000-4000-8000-000000000003",
        created_at: d(76),
        title: "Health Check-up Camp 2025",
        category: "healthcare",
        image_url: "/placeholder.svg",
        sort_order: 3,
      },
      {
        id: "o1000000-0000-4000-8000-000000000004",
        created_at: d(74),
        title: "Resident Garden Walk",
        category: "campus",
        image_url: "/placeholder.svg",
        sort_order: 4,
      },
    ],

    posts: [
      {
        id: "p1000000-0000-4000-8000-000000000001",
        created_at: d(30),
        title: "Jivandhara Completes 15 Years of Jivandhara",
        slug: "jivandhara-15-years",
        short_description: "Celebrating a decade and a half of compassionate elder care in Ahmedabad.",
        full_description: "Since 2011, Jivandhara Trust has served over 200 elders through Jivandhara Old Age Home...",
        category: "news",
        author: "Jivandhara Communications",
        status: "published",
        publish_date: day(-30),
        show_on_homepage: true,
      },
      {
        id: "p1000000-0000-4000-8000-000000000002",
        created_at: d(20),
        title: "New Medical Ward Campaign Launched",
        slug: "medical-ward-campaign",
        short_description: "Help us build a dedicated medical ward for our 48 residents.",
        category: "campaign",
        author: "Fundraising Team",
        status: "published",
        publish_date: day(-20),
        show_on_homepage: true,
      },
      {
        id: "p1000000-0000-4000-8000-000000000003",
        created_at: d(10),
        title: "Paryushan 2026 — Join Our Celebrations",
        slug: "paryushan-2026-announcement",
        short_description: "Spiritual discourses and special sattvic meals during the holy days.",
        category: "events",
        author: "Spiritual Committee",
        status: "published",
        publish_date: day(-10),
        show_on_homepage: false,
      },
    ],

    payment_settings: [
      {
        id: IDS.paymentSettings,
        created_at: d(30),
        updated_at: d(5),
        account_name: "Jivandhara Charitable Trust",
        bank_name: "Bank of Baroda",
        branch: "Navrangpura, Ahmedabad",
        account_no: "01234567890123",
        ifsc: "BARB0NAVRAN",
        upi_id: "jivandhara@upi",
        pan: "AAATM1234A",
        reg_80g_no: "CIT(E)/80G/123/2020-21",
        trust_reg_no: "E/1234/Ahmedabad",
        trust_address: "Jivandhara Old Age Home, Near SG Highway, Ahmedabad — 380054",
        contact_email: "donations@jivandhara.org",
        contact_phone: "+91 98765 43210",
        gateway_enabled: true,
        gateway_provider: "razorpay",
        offline_instructions: "Cheque / DD payable to 'Jivandhara Charitable Trust'. NEFT details above. WhatsApp receipt to +91 98765 43210.",
      },
    ],

    residents: [
      {
        id: IDS.resident1,
        created_at: d(400),
        updated_at: d(5),
        full_name: "Chhaganlal Popatlal Shah",
        resident_code: "RES-001",
        gender: "male",
        date_of_birth: "1942-03-15",
        admission_date: "2018-08-01",
        room_no: "A-12",
        status: "active",
        jain_status: "Shwetambar",
        religion: "Jain",
        city: "Ahmedabad",
        state: "Gujarat",
        previous_address: "Satellite, Ahmedabad",
        reason_for_admission: "No family support; requires assisted living",
        monthly_maintenance: 8000,
        blood_group: "B+",
      },
      {
        id: IDS.resident2,
        created_at: d(380),
        updated_at: d(5),
        full_name: "Kantaben Harilal Mehta",
        resident_code: "RES-002",
        gender: "female",
        date_of_birth: "1948-11-22",
        admission_date: "2019-02-14",
        room_no: "B-05",
        status: "active",
        jain_status: "Shwetambar",
        religion: "Jain",
        city: "Surat",
        state: "Gujarat",
        reason_for_admission: "Widow; children settled abroad",
        monthly_maintenance: 7500,
        blood_group: "O+",
      },
      {
        id: IDS.resident3,
        created_at: d(360),
        updated_at: d(5),
        full_name: "Mansukhlal Dahyabhai Patel",
        resident_code: "RES-003",
        gender: "male",
        date_of_birth: "1939-07-08",
        admission_date: "2020-06-10",
        room_no: "A-08",
        status: "active",
        jain_status: "Shwetambar",
        city: "Vadodara",
        state: "Gujarat",
        reason_for_admission: "Mobility issues; needs daily care",
        monthly_maintenance: 9000,
        blood_group: "A+",
      },
      {
        id: IDS.resident4,
        created_at: d(340),
        updated_at: d(5),
        full_name: "Jashuben Ramesh Desai",
        resident_code: "RES-004",
        gender: "female",
        date_of_birth: "1951-01-30",
        admission_date: "2021-11-05",
        room_no: "C-02",
        status: "active",
        jain_status: "Shwetambar",
        city: "Rajkot",
        state: "Gujarat",
        reason_for_admission: "Family recommended admission after spouse passed",
        monthly_maintenance: 7000,
        blood_group: "AB+",
      },
    ],

    resident_family: [
      {
        id: "q1000000-0000-4000-8000-000000000001",
        created_at: d(399),
        resident_id: IDS.resident1,
        name: "Jayesh Chhaganlal Shah",
        relation: "Son",
        mobile: "9898012345",
        email: "jayesh.shah@example.com",
        city: "Mumbai",
        state: "Maharashtra",
        is_primary: true,
        is_emergency: true,
      },
      {
        id: "q1000000-0000-4000-8000-000000000002",
        created_at: d(379),
        resident_id: IDS.resident2,
        name: "Neha Mehta",
        relation: "Daughter",
        mobile: "9898012346",
        email: "neha.mehta@example.com",
        city: "London",
        state: "UK",
        is_primary: true,
        is_emergency: true,
      },
      {
        id: "q1000000-0000-4000-8000-000000000003",
        created_at: d(359),
        resident_id: IDS.resident3,
        name: "Dinesh Patel",
        relation: "Nephew",
        mobile: "9898012347",
        city: "Vadodara",
        state: "Gujarat",
        is_primary: true,
        is_emergency: false,
      },
      {
        id: "q1000000-0000-4000-8000-000000000004",
        created_at: d(339),
        resident_id: IDS.resident4,
        name: "Ramesh Desai",
        relation: "Son",
        mobile: "9898012348",
        email: "ramesh.desai@example.com",
        city: "Rajkot",
        state: "Gujarat",
        is_primary: true,
        is_emergency: true,
      },
    ],

    contact_inquiries: [
      {
        id: "r1000000-0000-4000-8000-000000000001",
        created_at: d(15),
        full_name: "Suresh Agarwal",
        mobile: "9876001234",
        email: "suresh.agarwal@example.com",
        subject: "Admission enquiry for mother",
        message: "My mother is 78 and needs assisted living. Please share admission process and fees.",
        status: "open",
      },
      {
        id: "r1000000-0000-4000-8000-000000000002",
        created_at: d(12),
        full_name: "Pooja Shah",
        mobile: "9876001235",
        email: "pooja.shah@example.com",
        subject: "Volunteer for health camp",
        message: "I am a nurse and would like to volunteer for the upcoming health check-up camp.",
        status: "in_progress",
      },
      {
        id: "r1000000-0000-4000-8000-000000000003",
        created_at: d(8),
        full_name: "Hitesh Kumar",
        mobile: "9876001236",
        subject: "Corporate CSR partnership",
        message: "Our company wishes to explore CSR partnership for infrastructure projects.",
        status: "open",
      },
    ],

    feedback: [
      {
        id: "s1000000-0000-4000-8000-000000000001",
        created_at: d(25),
        full_name: "Deepak Jain",
        mobile: "9876101234",
        email: "deepak.jain@example.com",
        feedback_type: "donor",
        rating: 5,
        message: "Excellent transparency and warm communication. Proud to support Jivandhara.",
        status: "published",
        admin_reply: "Thank you for your kind words and continued support!",
      },
      {
        id: "s1000000-0000-4000-8000-000000000002",
        created_at: d(18),
        full_name: "Family of Chhaganlal Shah",
        mobile: "9898012345",
        feedback_type: "family",
        rating: 5,
        message: "The care my father receives is exceptional. Staff treats him like family.",
        status: "published",
      },
      {
        id: "s1000000-0000-4000-8000-000000000003",
        created_at: d(10),
        full_name: "Visitor — Priya M.",
        mobile: "9876101235",
        feedback_type: "visitor",
        rating: 4,
        message: "Peaceful campus and well-maintained facilities. Would recommend visiting.",
        status: "pending",
      },
    ],

    tiffin_requests: [
      {
        id: "t1000000-0000-4000-8000-000000000001",
        created_at: d(20),
        full_name: "Arvind Modi",
        mobile: "9876201234",
        email: "arvind.modi@example.com",
        city: "Ahmedabad",
        meal_type: "jain",
        tiffin_count: 50,
        start_date: day(10),
        delivery_required: false,
        status: "confirmed",
        special_instructions: "No onion, no garlic — strict Jain diet",
      },
      {
        id: "t1000000-0000-4000-8000-000000000002",
        created_at: d(14),
        full_name: "Bhavna Shah",
        mobile: "9876201235",
        city: "Gandhinagar",
        meal_type: "sattvic",
        tiffin_count: 25,
        start_date: day(5),
        delivery_required: true,
        delivery_address: "Sector 21, Gandhinagar",
        status: "pending",
      },
      {
        id: "t1000000-0000-4000-8000-000000000003",
        created_at: d(7),
        full_name: "Corporate — ABC Ltd",
        mobile: "9876201236",
        email: "csr@abc-ltd.com",
        city: "Ahmedabad",
        meal_type: "standard",
        tiffin_count: 100,
        start_date: day(30),
        status: "pending",
        special_instructions: "Company anniversary Annadan seva",
      },
    ],

    inkind_requests: [
      {
        id: "u1000000-0000-4000-8000-000000000001",
        created_at: d(22),
        donor_name: "Sanjay Verma",
        mobile: "9876301234",
        email: "sanjay.verma@example.com",
        item_name: "Wheelchairs (2 units)",
        item_category: "medical",
        quantity: "2",
        city: "Ahmedabad",
        pickup_required: true,
        address: "Satellite, Ahmedabad",
        message: "Gently used wheelchairs in good condition. Can arrange pickup on weekends.",
        status: "pending",
      },
      {
        id: "u1000000-0000-4000-8000-000000000002",
        created_at: d(11),
        donor_name: "Laxmi Trading Co.",
        mobile: "9876301235",
        item_name: "Rice and Dal — 50 kg each",
        item_category: "groceries",
        quantity: "100 kg total",
        city: "Ahmedabad",
        pickup_required: false,
        message: "Will deliver directly to campus gate.",
        status: "approved",
      },
    ],

    profiles: [
      {
        id: IDS.adminUser,
        created_at: d(500),
        email: "admin@jivandhara.org",
        full_name: "Admin User",
        phone: "+91 98765 43210",
      },
      {
        id: IDS.donorUser,
        created_at: d(300),
        email: "ramesh.patel@example.com",
        full_name: "Ramesh Chimanlal Patel",
        phone: "9876543210",
      },
    ],

    user_roles: [
      {
        id: "v1000000-0000-4000-8000-000000000001",
        created_at: d(500),
        user_id: IDS.adminUser,
        role: "super_admin",
      },
      {
        id: "v1000000-0000-4000-8000-000000000002",
        created_at: d(300),
        user_id: IDS.donorUser,
        role: "donor",
      },
    ],

    receipts: [
      {
        id: "w1000000-0000-4000-8000-000000000001",
        created_at: d(1),
        receipt_no: "MSV-20250001",
        donation_id: donationIds[0],
        donor_id: IDS.donor1,
        fy: "2025-26",
        status: "generated",
        generated_at: d(1),
        is_consolidated: false,
        sent_via: "email",
        sent_at: d(1),
      },
      {
        id: "w1000000-0000-4000-8000-000000000002",
        created_at: d(2),
        receipt_no: "MSV-20250002",
        donation_id: donationIds[1],
        donor_id: IDS.donor2,
        fy: "2025-26",
        status: "generated",
        generated_at: d(2),
        is_consolidated: false,
      },
      {
        id: "w1000000-0000-4000-8000-000000000003",
        created_at: d(4),
        receipt_no: "MSV-20250003",
        donation_id: donationIds[3],
        donor_id: IDS.donor4,
        fy: "2025-26",
        status: "generated",
        generated_at: d(4),
        is_consolidated: false,
        sent_via: "whatsapp",
        sent_at: d(4),
      },
    ],
  };
}
